<template>
    <div>
    <ValidationObserver ref="observer" v-slot="{ handleSubmit, invalid, validated }">
      <!-- Global Header -->
      <v-row class="mb-8 align-center px-4 pt-4">
        <v-btn icon @click.stop="$router.go(-1)" class="mr-4 grey lighten-4 shadow-sm">
          <v-icon color="primary" small>fas fa-chevron-left</v-icon>
        </v-btn>
        <div class="d-flex align-center">
          <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
            <v-icon color="white">fas fa-file-invoice-dollar</v-icon>
          </v-avatar>
          <div>
            <div class="text-h4 font-weight-black primary--text lh-1">
              {{ isEditing ? 'Editar' : 'Nueva' }} Factura de Compra
            </div>
            <div class="mt-1 d-flex align-center">
              <v-chip v-if="isEditing" :color="getStatusColor(invoice.estatus)" dark x-small label class="font-weight-black px-2 mr-2">
                {{ getStatusName(invoice.estatus) }}
              </v-chip>
              <span class="caption grey--text font-weight-bold uppercase tracking-widest">
                {{ isEditing ? `Documento #${invoice.document_id}` : 'Registro de documentos de proveedores' }}
              </span>
            </div>
          </div>
        </div>
        <v-spacer></v-spacer>
        
        <v-btn 
          :color="invalid && validated ? 'error' : 'success'" 
          class="font-weight-black shadow-sm px-8 h-44" 
          @click="handleSubmit(saveInvoice)" 
          :loading="loading"
        >
          <v-icon left small>{{ invalid && validated ? 'fas fa-exclamation-triangle' : 'fas fa-save' }}</v-icon> 
          {{ invalid && validated ? 'Corregir Errores' : 'Guardar Factura' }}
        </v-btn>
      </v-row>

      <v-form @submit.prevent="handleSubmit(saveInvoice)" @keydown.enter.prevent>
        <v-row>
          <!-- Sidebar: Metadata and Provider Info -->
          <v-col cols="12" md="3">
            <!-- Linking Card -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-8">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Vinculación de Órdenes</div>
              
              <div class="caption grey--text font-weight-black mb-1 ml-1">Orden de Compra (OC)</div>
              <v-text-field
                v-model="purchase_order_search"
                placeholder="N° OC..."
                outlined dense
                class="modern-input mb-4"
                :append-icon="purchase_order_finded ? 'fas fa-check-circle' : 'fas fa-search'"
                :loading="loadingPurchaseOrder"
                @keydown="searchPurchaseOrder"
                :hint="alertHasInvoices ? 'Ya existe un documento para esta OC' : ''"
                :persistent-hint="alertHasInvoices"
                :success="purchase_order_finded"
                hide-details="auto"
              >
                <template v-slot:append v-if="purchase_order_finded">
                  <v-icon small @click="clearPurchaseOrder" color="error">fas fa-times-circle</v-icon>
                </template>
              </v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Orden de Trabajo (OT)</div>
              <v-text-field
                v-model="work_order_search"
                placeholder="N° OT..."
                outlined dense
                class="modern-input mb-4"
                :append-icon="work_order_finded ? 'fas fa-check-circle' : 'fas fa-search'"
                :loading="loadingWorkOrder"
                @keydown="searchWorkOrder"
                :success="work_order_finded"
                hide-details="auto"
              >
                <template v-slot:append v-if="work_order_finded">
                  <v-icon small @click="clearWorkOrder" color="error">fas fa-times-circle</v-icon>
                </template>
              </v-text-field>

              <v-slide-y-transition>
                <div v-if="work_order_finded" class="pa-4 bg-lightgrey rounded-lg border-dashed">
                  <div class="caption grey--text font-weight-black mb-1">DETALLES DE LA OT</div>
                  <div class="body-2 font-weight-bold grey--text text--darken-3 lh-1-2">{{ work_order_details }}</div>
                </div>
              </v-slide-y-transition>
            </v-card>

            <!-- Document Info Card -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-8">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Datos del Documento</div>
              
              <div class="caption grey--text font-weight-black mb-1 ml-1">Tipo de Documento</div>
              <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
                <v-select
                  v-model="invoice.tipo"
                  :items="tipos"
                  item-text="text"
                  item-value="value"
                  outlined dense
                  class="modern-input mb-4"
                  :error-messages="errors"
                  hide-details="auto"
                />
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Nro Documento / Folio</div>
              <ValidationProvider name="Folio" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="invoice.document_id"
                  placeholder="Ej: 45620"
                  outlined dense
                  class="modern-input mb-4"
                  :error-messages="errors"
                  hide-details="auto"
                />
              </ValidationProvider>

              <v-row dense>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Fecha Recib.</div>
                  <ValidationProvider name="Fecha" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="invoice.date_received"
                      type="date"
                      outlined dense
                      class="modern-input"
                      :error-messages="errors"
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Vencim. (Días)</div>
                  <ValidationProvider name="Condición" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="invoice.payment_condition"
                      type="number"
                      outlined dense
                      class="modern-input"
                      :error-messages="errors"
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card>

            <!-- Provider Info Card -->
            <v-card outlined class="modern-card shadow-sm pa-6">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Datos del Emisor</div>
              
              <div class="caption grey--text font-weight-black mb-1 ml-1">RUT Proveedor</div>
              <ValidationProvider name="RUT" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="invoice.rutEmisor"
                  placeholder="11.111.111-1"
                  outlined dense
                  class="modern-input mb-4"
                  :error-messages="errors"
                  hide-details="auto"
                />
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Razón Social</div>
              <ValidationProvider name="Razón Social" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="invoice.rznSocEmisor"
                  placeholder="Nombre de la empresa..."
                  outlined dense
                  class="modern-input mb-4"
                  :error-messages="errors"
                  hide-details="auto"
                />
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Impuesto Local (%)</div>
              <ValidationProvider name="IVA" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="invoice.iva"
                  type="number"
                  outlined dense
                  class="modern-input"
                  :error-messages="errors"
                  hide-details="auto"
                  @input="calculateTotals"
                />
              </ValidationProvider>
            </v-card>
          </v-col>

          <!-- Main Content: Items Detail -->
          <v-col cols="12" md="9">
            <div class="mb-4 d-flex align-center">
              <v-avatar color="primary" size="32" class="mr-3 shadow-xs">
                <v-icon color="white" x-small>fas fa-list-ul</v-icon>
              </v-avatar>
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest">
                Detalles del Documento / Recepción
              </div>
              <v-spacer></v-spacer>
              <v-btn
                color="success"
                class="font-weight-black px-6 rounded-lg shadow-sm"
                @click="addDetail"
              >
                <v-icon left small>fas fa-plus</v-icon> Agregar Línea
              </v-btn>
            </div>

            <v-card outlined class="modern-card shadow-sm overflow-hidden mb-8">
              <v-data-table
                :headers="detailHeaders"
                :items="invoice.details"
                item-key="nro_linea"
                :loading="loading"
                hide-default-footer
                class="modern-table"
                :items-per-page="-1"
              >
                <template v-slot:no-data>
                  <div class="pa-10 text-center fill-width grey--text">
                    <v-icon size="48" color="grey lighten-3" class="mb-4">fas fa-receipt</v-icon>
                    <div class="body-1 font-weight-bold uppercase mb-2">No hay líneas en la factura</div>
                    <div class="caption">Agregue ítems para calcular los impuestos y totales</div>
                  </div>
                </template>

                <template v-slot:item.nro_linea="{ item }">
                  <span class="caption font-weight-black grey--text">{{ item.nro_linea }}</span>
                </template>

                <template v-slot:item.sku="{ item }">
                  <v-text-field
                    v-model="item.sku"
                    outlined dense hide-details
                    class="modern-input-minimal font-weight-black primary--text"
                    placeholder="SKU..."
                  ></v-text-field>
                </template>

                <template v-slot:item.name="{ item }">
                  <v-textarea
                    v-model="item.name"
                    outlined dense hide-details
                    rows="1"
                    auto-grow
                    class="modern-input-minimal body-2 py-1"
                    placeholder="Descripción..."
                    style="min-width: 0"
                  ></v-textarea>
                </template>

                <template v-slot:item.qty="{ item }">
                  <v-text-field
                    v-model.number="item.qty"
                    type="number"
                    outlined dense hide-details
                    class="modern-input-minimal text-center"
                    @input="calculateTotals"
                  ></v-text-field>
                </template>

                <template v-slot:item.unit="{ item }">
                  <v-text-field
                    v-model="item.unit"
                    outlined dense hide-details
                    class="modern-input-minimal text-center"
                  ></v-text-field>
                </template>

                <template v-slot:item.price="{ item }">
                  <v-text-field
                    v-model.number="item.price"
                    type="number"
                    outlined dense hide-details
                    class="modern-input-minimal text-right font-weight-bold"
                    @input="calculateTotals"
                  ></v-text-field>
                </template>

                <template v-slot:item.total="{ item }">
                  <span class="body-2 font-weight-black primary--text px-2">
                    {{ (item.qty * item.price) | moneda }}
                  </span>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn icon small color="error" class="grey lighten-4" @click="removeDetailByItem(item)">
                    <v-icon x-small>fas fa-trash</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>

            <!-- Totals Card -->
            <v-card outlined class="modern-card shadow-sm pa-8 bg-lightgrey">
              <v-row align="center">
                <v-col cols="12" md="3" class="border-right px-6 text-center">
                  <div class="caption grey--text font-weight-black uppercase mb-1">Subtotal Neto</div>
                  <div class="text-h5 font-weight-black primary--text">{{ subtotal | moneda }}</div>
                </v-col>
                
                <v-col cols="12" md="4" class="border-right px-6 text-center">
                  <div class="d-flex align-center justify-center mb-1">
                    <span class="caption grey--text font-weight-black uppercase mr-2">Tax ({{ invoice.iva }}%)</span>
                  </div>
                  <div class="text-h6 font-weight-bold grey--text text--darken-3">
                    {{ ivaAmount | moneda }}
                  </div>
                </v-col>

                <v-col cols="12" md="5" class="px-8 d-flex flex-column align-end">
                  <div class="caption primary--text font-weight-black uppercase tracking-wider mb-1">Total Documento</div>
                  <div class="text-h3 font-weight-black primary--text tracking-tighter">
                    {{ total | moneda }}
                  </div>
                  <div v-if="invoice.date_received" class="caption grey--text font-weight-bold mt-2">
                    Vencimiento Estimado: <span class="primary--text">{{ getExpiryDate() }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>

    <confirm 
      v-model="showConfirm" 
      :message="isEditing ? 'Se actualizarán los datos de la factura. ¿Desea continuar?' : 'Se registrará una nueva factura de compra. ¿Desea continuar?'" 
      @ok="saveInvoice"
      @cancel="showConfirm=false"
    />
  </div>
</template>

<script>
import { 
  getPurchaseInvoice, 
  createPurchaseInvoice, 
  updatePurchaseInvoice 
} from '@/api/purchases'
import { getPurchase } from '@/api/stocks'
import { getWorkOrder } from '@/api/production'
import notifications from '@/utils/notifications'
import moment from 'moment'

export default {
  name: 'CreatePurchaseInvoice',
  data() {
    return {
      loading: false,
      isEditing: false,
      showConfirm: false,
      purchase_order_search: '',
      purchase_order_finded: false,
      loadingPurchaseOrder: false,
      alertHasInvoices: false,
      timer: null,
      work_order_search: '',
      work_order_finded: false,
      work_order_details: '',
      loadingWorkOrder: false,
      workOrderTimer: null,
      
      invoice: {
        document_id: '',
        tipo: 30,
        date_received: new Date().toISOString().substr(0, 10),
        provider: null,
        rutEmisor: '',
        rznSocEmisor: '',
        iva: parseInt(this.$store.getters.getCompany.tax),
        payment_condition: 30,
        estatus: 1,
        purchase_order: null,
        work_order: null,
        details: []
      },
      
      detailHeaders: [
        { text: '#', value: 'nro_linea', width: '60px', class: 'table-header' },
        { text: 'SKU', value: 'sku', width: '130px', class: 'table-header' },
        { text: 'Descripción del Ítem', value: 'name', class: 'table-header' },
        { text: 'Cant.', value: 'qty', width: '110px', align: 'center', class: 'table-header' },
        { text: 'Unidad', value: 'unit', width: '110px', align: 'center', class: 'table-header' },
        { text: 'Precio Unit.', value: 'price', width: '155px', align: 'end', class: 'table-header' },
        { text: 'Total', value: 'total', width: '130px', align: 'end', class: 'table-header' },
        { text: '', value: 'actions', width: '50px', align: 'center', class: 'table-header', sortable: false }
      ],
      
      tipos: [
        { value: 30, text: 'Factura' },
        { value: 50, text: 'Guía de Despacho' },
        { value: 60, text: 'Nota de Crédito' }
      ],
    }
  },
  
  computed: {
    subtotal() {
      return this.invoice.details.reduce((sum, detail) => sum + (parseFloat(detail.qty || 0) * parseFloat(detail.price || 0)), 0)
    },
    ivaAmount() {
      return this.subtotal * (this.invoice.iva / 100)
    },
    total() {
      return this.subtotal + this.ivaAmount
    },
    validated() {
      return this.$refs.observer ? this.$refs.observer.flags.validated : false
    }
  },
  
  methods: {
    getExpiryDate() {
      if (!this.invoice.date_received) return '-'
      return moment(this.invoice.date_received).add(this.invoice.payment_condition, 'days').format('DD/MM/YYYY')
    },
    searchPurchaseOrder() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        if (!this.purchase_order_search) return
        this.loadingPurchaseOrder = true
        try {
          const data = await getPurchase(this.purchase_order_search)
          this.purchase_order_finded = true
          this.invoice.purchase_order = data.id
          this.invoice.work_order = data.work_order || null
          
          if (data.provider) {
            this.invoice.rutEmisor = data.provider_rut || data.provider.rut || ''
            this.invoice.rznSocEmisor = data.provider_name || data.provider.name || ''
          }
          
          this.alertHasInvoices = data.has_invoices || false
          this.invoice.iva = data.iva || this.$store.getters.getCompany.tax
          
          if (data.work_order) {
            this.work_order_search = data.work_order.toString()
            this.searchWorkOrder()
          }
          
          if (data.details && data.details.length > 0) {
            this.invoice.details = data.details.map((detail, idx) => ({
              nro_linea: idx + 1,
              sku: detail.article_sku || detail.sku || '-',
              name: detail.article_name || detail.name || '',
              qty: detail.qty || 1,
              unit: detail.article_unit_name || 'UN',
              price: detail.price || 0,
              article: detail.article || null
            }))
          }
        } catch (error) {
          this.purchase_order_finded = false
          notifications.showInfo('Orden de Compra no encontrada')
        } finally {
          this.loadingPurchaseOrder = false
        }
      }, 800)
    },
    
    clearPurchaseOrder() {
      this.purchase_order_search = ''
      this.purchase_order_finded = false
      this.alertHasInvoices = false
      this.invoice.purchase_order = null
    },
    
    searchWorkOrder() {
      if (this.workOrderTimer) clearTimeout(this.workOrderTimer)
      this.workOrderTimer = setTimeout(async () => {
        if (!this.work_order_search) return
        this.loadingWorkOrder = true
        try {
          const data = await getWorkOrder(this.work_order_search)
          this.work_order_finded = true
          const { customer_name, created_at, name } = data
          this.work_order_details = `${customer_name} ${moment(created_at).format('DD/MM/YYYY')} ${name}`
          this.invoice.work_order = this.work_order_search
        } catch (error) {
          this.work_order_finded = false
          notifications.showInfo('Orden de Trabajo no encontrada')
        } finally {
          this.loadingWorkOrder = false
        }
      }, 1000)
    },
    
    clearWorkOrder() {
      this.work_order_search = ''
      this.work_order_finded = false
      this.work_order_details = ''
      this.invoice.work_order = null
    },
    
    getStatusName(status) {
      const statusMap = { 1: 'Recibida', 2: 'Pagada', 3: 'Anulada' }
      return statusMap[status] || 'Desconocido'
    },
    
    getStatusColor(status) {
      const colorMap = { 1: 'info', 2: 'success', 3: 'error' }
      return colorMap[status] || 'grey'
    },
    
    async loadInvoice() {
      if (this.isEditing && this.$route.params.id) {
        try {
          this.loading = true
          const response = await getPurchaseInvoice(this.$route.params.id)
          this.invoice = { ...response }
          if (this.invoice.purchase_order) {
            this.purchase_order_search = this.invoice.purchase_order.toString()
            this.purchase_order_finded = true
          }
          if (this.invoice.work_order) {
            this.work_order_search = this.invoice.work_order.toString()
            this.work_order_finded = true
            this.searchWorkOrder()
          }
        } catch (error) {
          notifications.showError('Error cargando factura')
          this.$router.push({ name: 'PurchaseInvoices' })
        } finally {
          this.loading = false
        }
      }
    },
    
    addDetail() {
      this.invoice.details.push({
        nro_linea: this.invoice.details.length + 1,
        sku: '',
        name: '',
        qty: 1,
        unit: 'UN',
        price: 0,
        article: null
      })
    },

    removeDetailByItem(item) {
      const index = this.invoice.details.findIndex(detail => detail.nro_linea === item.nro_linea)
      if (index !== -1) {
        this.invoice.details.splice(index, 1)
        this.invoice.details.forEach((detail, idx) => { detail.nro_linea = idx + 1 })
      }
    },
    
    calculateTotals() {
      // Automatic in computed
    },
    
    async saveInvoice() {
      if (this.invoice.details.length <= 0) {
        notifications.showError('Deben existir elementos en la factura')
        return
      }

      this.loading = true
      try {
        const dataToSend = JSON.parse(JSON.stringify(this.invoice))
        dataToSend.details.forEach(detail => { if (!detail.sku) detail.sku = '-' })
        
        if (!dataToSend.purchase_order || !this.purchase_order_finded) delete dataToSend.purchase_order
        if (!dataToSend.work_order || !this.work_order_finded) delete dataToSend.work_order
        
        if (this.isEditing) {
          await updatePurchaseInvoice(this.$route.params.id, dataToSend)
          notifications.showSuccess('Factura actualizada correctamente')
        } else {
          await createPurchaseInvoice(dataToSend)
          notifications.showSuccess('Factura creada correctamente')
        }
        this.$router.go(-1)
      } catch (error) {
        notifications.showError('Error guardando factura: ' + error.message)
      } finally {
        this.loading = false
      }
    },
    
    async initializeComponent() {
      const routeId = this.$route.params.id
      this.isEditing = routeId && routeId !== 'new' && routeId !== undefined && routeId !== 'undefined'
      
      if (this.isEditing) {
        await this.loadInvoice()
      }
      if (this.invoice.details.length === 0) {
        this.addDetail()
      }
    }
  },
  
  async mounted() {
    await this.initializeComponent()
  },
  
  watch: {
    '$route'(to, from) {
      if (to.params.id !== from.params.id) this.initializeComponent()
    },
    purchase_order_search(newVal) {
      if (!newVal) this.clearPurchaseOrder()
    },
    work_order_search(newVal) {
      if (!newVal) this.clearWorkOrder()
    }
  }
}
</script>

<style scoped>
.modern-detail-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; transition: all 0.3s ease; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-dashed { border: 1px dashed #e0e4e8 !important; }
.border-right { border-right: 1px solid rgba(0,0,0,0.05) !important; }
.border-error-light { border: 1px solid rgba(var(--v-error-base), 0.3) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1.5px; }
.h-44 { height: 44px !important; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }

.modern-input-minimal >>> .v-input__control fieldset { border-radius: 6px !important; border-color: #f0f3f5 !important; }
.modern-input-minimal >>> input, .modern-input-minimal >>> textarea { font-size: 0.85rem !important; padding: 2px 6px !important; }
.modern-input-minimal >>> .v-input__slot { min-height: 32px !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 38px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; padding: 0 8px !important; }
.modern-table >>> td { height: 46px !important; border-bottom: 1px solid #f0f3f5 !important; padding: 4px 8px !important; }

.v-btn.shadow-sm { box-shadow: 0 4px 10px rgba(0,0,0,0.1) !important; }
</style>